import { useRef, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import {
  AlertTriangle, CheckCircle2, FileText, Globe2, Sparkles, Upload,
} from "lucide-react";
import { ScoreBadge, Stat, SimpleMd } from "@/components/app/ContentStudio.parts";
import OtterIcon from "@/components/app/OtterIcon";

/**
 * Standalone "Analyze" tab — paste content, an URL, or upload a file
 * and benchmark it against a target keyword. No editor state required.
 */
export default function AnalyzeTab({ projectId }) {
  const [aMode, setAMode] = useState("paste"); // paste | url | upload
  const [aUploading, setAUploading] = useState(false);
  const uploadInputRef = useRef(null);
  const [aKeyword, setAKeyword] = useState("");
  const [aText, setAText] = useState("");
  const [aUrl, setAUrl] = useState("");
  const [aScore, setAScore] = useState(null);
  const [aAi, setAAi] = useState("");
  const [aBusy, setABusy] = useState(false);

  const runAnalyze = async (useAi = false) => {
    if (!aKeyword.trim()) { toast.error("Add a target keyword first"); return; }
    if (aMode === "paste" && !aText.trim()) { toast.error("Paste some content first"); return; }
    if (aMode === "url" && !aUrl.trim()) { toast.error("Enter a URL"); return; }
    if (aMode === "upload" && !aText.trim()) { toast.error("Upload a file first"); return; }
    setABusy(true); setAAi("");
    try {
      const payload = { target_keyword: aKeyword, use_ai: useAi };
      if (aMode === "url") payload.url = aUrl;
      else payload.content = aText;
      const { data } = await api.post(`/projects/${projectId}/content/analyze`, payload);
      setAScore(data.score);
      if (data.ai_feedback) setAAi(data.ai_feedback);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Failed"); }
    setABusy(false);
  };

  const handleUpload = async (file) => {
    if (!file) return;
    const name = (file.name || "").toLowerCase();
    if (!/\.(md|markdown|txt|docx)$/.test(name)) {
      toast.error("Supported: .md, .txt, .docx (5MB max)");
      return;
    }
    setAUploading(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await api.post(`/projects/${projectId}/content/upload`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setAText(data.text || "");
      toast.success(`Loaded ${data.source_filename} · ${data.word_count} words`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Upload failed");
    }
    setAUploading(false);
  };

  return (
    <div className="mt-4 space-y-4">
      <p className="text-sm text-muted-foreground">Paste content or analyze a published URL against a target keyword.</p>
      <div className="grid lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 border border-border bg-white p-4 space-y-3">
          <div className="flex gap-2 flex-wrap">
            <button onClick={()=>setAMode("paste")} className={`px-3 py-1.5 text-xs font-mono-data uppercase tracking-widest border ${aMode==="paste" ? "bg-foreground text-background border-foreground" : "border-border"}`} data-testid="analyze-mode-paste">
              <FileText className="inline w-3.5 h-3.5 mr-1" /> Paste content
            </button>
            <button onClick={()=>setAMode("url")} className={`px-3 py-1.5 text-xs font-mono-data uppercase tracking-widest border ${aMode==="url" ? "bg-foreground text-background border-foreground" : "border-border"}`} data-testid="analyze-mode-url">
              <Globe2 className="inline w-3.5 h-3.5 mr-1" /> Live URL
            </button>
            <button onClick={()=>setAMode("upload")} className={`px-3 py-1.5 text-xs font-mono-data uppercase tracking-widest border ${aMode==="upload" ? "bg-foreground text-background border-foreground" : "border-border"}`} data-testid="analyze-mode-upload">
              <Upload className="inline w-3.5 h-3.5 mr-1" /> Upload file
            </button>
          </div>
          <div className="space-y-2">
            <Label className="font-mono-data text-xs uppercase tracking-widest">Target keyword</Label>
            <Input value={aKeyword} onChange={(e)=>setAKeyword(e.target.value)} placeholder="e.g. 'remote work productivity'"
              className="rounded-none h-11 border-foreground/30 focus-visible:ring-foreground"
              data-testid="analyze-keyword" />
          </div>
          {aMode === "paste" && (
            <div className="space-y-2">
              <Label className="font-mono-data text-xs uppercase tracking-widest">Content (markdown or plain text)</Label>
              <Textarea value={aText} onChange={(e)=>setAText(e.target.value)} placeholder="# Your draft headline&#10;&#10;Paste the full article here…"
                className="rounded-none border-foreground/30 focus-visible:ring-foreground font-mono-data text-sm min-h-[360px]"
                data-testid="analyze-textarea" />
            </div>
          )}
          {aMode === "url" && (
            <div className="space-y-2">
              <Label className="font-mono-data text-xs uppercase tracking-widest">URL to analyze</Label>
              <Input value={aUrl} onChange={(e)=>setAUrl(e.target.value)} placeholder="https://yoursite.com/blog/post"
                className="rounded-none h-11 border-foreground/30 focus-visible:ring-foreground"
                data-testid="analyze-url" />
            </div>
          )}
          {aMode === "upload" && (
            <div className="space-y-2">
              <Label className="font-mono-data text-xs uppercase tracking-widest">Upload a draft (.md / .txt / .docx, ≤ 5MB)</Label>
              <div
                onDragOver={(e) => { e.preventDefault(); e.currentTarget.classList.add("bg-secondary/60"); }}
                onDragLeave={(e) => e.currentTarget.classList.remove("bg-secondary/60")}
                onDrop={(e) => {
                  e.preventDefault();
                  e.currentTarget.classList.remove("bg-secondary/60");
                  const f = e.dataTransfer.files?.[0];
                  if (f) handleUpload(f);
                }}
                className="border-2 border-dashed border-foreground/30 p-10 text-center transition-colors cursor-pointer"
                onClick={() => uploadInputRef.current?.click()}
                data-testid="analyze-upload-zone"
              >
                <input
                  ref={uploadInputRef}
                  type="file"
                  accept=".md,.markdown,.txt,.docx"
                  className="hidden"
                  onChange={(e) => handleUpload(e.target.files?.[0])}
                  data-testid="analyze-upload-input"
                />
                <Upload className="w-7 h-7 mx-auto" strokeWidth={1.5} />
                <p className="mt-3 font-semibold tracking-tight">{aUploading ? "Parsing…" : "Drop your file here"}</p>
                <p className="text-xs text-muted-foreground mt-1 font-mono-data uppercase tracking-widest">or click to browse</p>
              </div>
              {aText && (
                <div className="border border-border bg-white p-3" data-testid="analyze-upload-preview">
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-mono-data text-[10px] uppercase tracking-widest text-muted-foreground">parsed text · first 600 chars</span>
                    <button type="button" onClick={() => setAText("")} className="font-mono-data text-[10px] uppercase tracking-widest text-muted-foreground hover:text-destructive">
                      clear
                    </button>
                  </div>
                  <pre className="whitespace-pre-wrap font-mono-data text-xs leading-relaxed max-h-40 overflow-y-auto">{aText.slice(0, 600)}{aText.length > 600 ? "…" : ""}</pre>
                </div>
              )}
            </div>
          )}
          <div className="flex flex-wrap gap-2">
            <Button onClick={()=>runAnalyze(false)} disabled={aBusy} variant="outline" className="rounded-none h-10" data-testid="analyze-run">
              {aBusy ? "Analyzing…" : "Analyze"}
            </Button>
            <Button onClick={()=>runAnalyze(true)} disabled={aBusy} className="rounded-none h-10 bg-foreground hover:bg-foreground/90" data-testid="analyze-run-ai">
              <Sparkles className="w-4 h-4 mr-1.5" /> {aBusy ? "Thinking…" : "Analyze + AI"}
            </Button>
          </div>
        </div>

        <div className="space-y-4">
          {aScore ? (
            <>
              <div className="border border-border bg-white p-4">
                <div className="flex items-center justify-between">
                  <h4 className="font-display tracking-tight text-base">Score</h4>
                  <ScoreBadge score={aScore.score} />
                </div>
                <div className="grid grid-cols-2 -mr-px -mb-px mt-3 text-xs">
                  <Stat label="Words" value={aScore.word_count} />
                  <Stat label="Read" value={`${aScore.reading_minutes}m`} />
                  <Stat label="Density" value={`${aScore.keyword_density}%`} />
                  <Stat label="Hits" value={aScore.keyword_count} />
                  <Stat label="Flesch" value={aScore.flesch} />
                  <Stat label="H2s" value={aScore.h2.length} />
                </div>
                <div className="mt-4 space-y-2 text-xs">
                  {aScore.issues.map((it,i)=>(<div key={`ai${i}`} className="flex gap-2"><AlertTriangle className="w-3.5 h-3.5 text-red-600 shrink-0 mt-0.5" /><span>{it}</span></div>))}
                  {aScore.warnings.map((it,i)=>(<div key={`aw${i}`} className="flex gap-2"><AlertTriangle className="w-3.5 h-3.5 text-amber-600 shrink-0 mt-0.5" /><span>{it}</span></div>))}
                  {aScore.passed.slice(0,5).map((it,i)=>(<div key={`ap${i}`} className="flex gap-2"><CheckCircle2 className="w-3.5 h-3.5 text-emerald-600 shrink-0 mt-0.5" /><span>{it}</span></div>))}
                </div>
              </div>
              {aAi && (
                <div className="border border-border bg-white p-4">
                  <h4 className="font-display tracking-tight text-base flex items-center gap-2"><Sparkles className="w-4 h-4" /> AI editor</h4>
                  <div className="mt-3"><SimpleMd text={aAi} /></div>
                </div>
              )}
            </>
          ) : (
            <div className="border border-dashed border-foreground/30 p-8 text-center">
              <div className="w-11 h-11 mx-auto bg-foreground grid place-items-center text-background mb-3">
                <OtterIcon size={26} />
              </div>
              <p className="text-sm text-muted-foreground">
                Arlo&apos;s standing by. Hit <em>Analyze</em> and he&apos;ll grade your draft on 12 SEO checks.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
